/**
 * Loads the Payroc Hosted Fields script from REACT_APP_PAYROC_HOSTED_FIELDS_SCRIPT_URL (webpack / local.env).
 */

let payrocScriptInjectPromise = null;

// eslint-disable-next-line import/prefer-default-export -- named export used by PayrocHostedPaymentSections
export function ensurePayrocHostedFieldsLoaded(timeoutMs = 45000) {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('Payroc requires a browser environment'));
  }
  if (window.Payroc && window.Payroc.hostedFields) {
    return Promise.resolve();
  }

  const url = (process.env.REACT_APP_PAYROC_HOSTED_FIELDS_SCRIPT_URL || '').trim();
  if (!url || !/^https?:\/\//i.test(url)) {
    return Promise.reject(new Error(
      'Payroc is not configured: set REACT_APP_PAYROC_HOSTED_FIELDS_SCRIPT_URL in rhinofront/local.env to the '
      + 'HTTPS Hosted Fields script URL for your tier (same value as rhinopay PAYROC_HOSTED_FIELDS_SCRIPT_URL), '
      + 'then restart the dev server.',
    ));
  }

  const waitForPayrocGlobal = () => new Promise((resolve, reject) => {
    const deadline = Date.now() + timeoutMs;
    const tick = () => {
      if (window.Payroc && window.Payroc.hostedFields) {
        resolve();
        return;
      }
      if (Date.now() > deadline) {
        reject(new Error(
          'Payroc Hosted Fields did not initialize. Check REACT_APP_PAYROC_HOSTED_FIELDS_SCRIPT_URL / SRI, '
          + 'browser console, and network tab for the script request.',
        ));
        return;
      }
      setTimeout(tick, 50);
    };
    tick();
  });

  const injectScript = () => new Promise((resolve, reject) => {
    let targetHref;
    try {
      targetHref = new URL(url).href;
    } catch (e) {
      reject(new Error('Invalid REACT_APP_PAYROC_HOSTED_FIELDS_SCRIPT_URL'));
      return;
    }

    const existing = Array.from(document.scripts).filter((el) => {
      if (!el.src) return false;
      try {
        return new URL(el.src).href === targetHref;
      } catch (err) {
        return false;
      }
    });

    if (window.Payroc && window.Payroc.hostedFields) {
      resolve();
      return;
    }

    existing.forEach((el) => {
      if (el.getAttribute('data-rhinogram-payroc-hosted-fields')) {
        el.remove();
      }
    });

    const integ = (process.env.REACT_APP_PAYROC_HOSTED_FIELDS_SRI_SHA384 || '').trim();
    let integrityAttr = null;
    if (integ) {
      integrityAttr = integ.indexOf('sha384-') === 0 ? integ : `sha384-${integ}`;
    }

    const appendScript = (useIntegrity, isRetry) => new Promise((res, rej) => {
      const s = document.createElement('script');
      s.async = true;
      s.src = url;
      s.crossOrigin = 'anonymous';
      s.setAttribute('data-rhinogram-payroc-hosted-fields', '1');
      if (useIntegrity && integrityAttr) {
        s.integrity = integrityAttr;
      }
      s.onload = () => res();
      s.onerror = () => {
        if (useIntegrity && integrityAttr && !isRetry) {
          // eslint-disable-next-line no-console -- local dev diagnostic when SRI blocks the bundle
          console.warn(
            '[Rhinopay] Payroc Hosted Fields script failed SRI check; retrying without integrity. '
            + 'Fix REACT_APP_PAYROC_HOSTED_FIELDS_SRI_SHA384 in local.env to match the script URL, or remove it for local dev.',
          );
          appendScript(false, true).then(res).catch(rej);
          return;
        }
        rej(new Error(
          `Failed to load Payroc Hosted Fields from ${url}. `
          + 'Check Network tab (status/blocked), SRI digest vs bundle, and that you restarted via yarn start after editing local.env.',
        ));
      };
      document.head.appendChild(s);
    });

    appendScript(true, false).then(resolve).catch(reject);
  });

  if (!payrocScriptInjectPromise) {
    payrocScriptInjectPromise = injectScript().catch((err) => {
      payrocScriptInjectPromise = null;
      throw err;
    });
  }

  return payrocScriptInjectPromise.then(() => waitForPayrocGlobal());
}
